import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import {
  LocalizationProvider,
  loadMessages,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../../AlertsGrid/GridLocale';
import { Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import esMessages from '../../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import StringResourceManager, { GetString } from '../../StringResourceManager';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { Cron } from 'react-js-cron';
import 'react-js-cron/dist/styles.css';
import { convertDateYYYYMMDD, maskString } from '../../../utils/utils';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import {
  InputPrefix,
  InputSuffix,
  TextBox,
} from '@progress/kendo-react-inputs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button } from '@progress/kendo-react-buttons';
import clsx from 'clsx';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Checkbox, FormControlLabel } from '@mui/material';
import NodeSelection from '../../NodeSelection';
import {
  SendToDivisionCommandInfo,
  SendToLanesCommandInfo,
  SendToNodeGroupsCommandInfo,
  SendToSiteNumbersCommandInfo,
} from '../../../Interfaces/Commands/CommandInfo';
import {
  clearSuccessMessage,
  sendScheduledToDivisionCommand,
  sendScheduledToLanesCommand,
  sendScheduledToNodeGroupsCommand,
  sendScheduledToSiteNumberCommand,
} from '../../../store/Assets/AssetsActions';
import {
  SendToDivisionsScheduledCommandInfo,
  SendToLanesScheduledCommandInfo,
  SendToNodeGroupsScheduledCommandInfo,
  SendToSiteNumbersScheduledCommandInfo,
} from '../../../Interfaces/Commands/ScheduledCommandInfo';
import { getDateCreatedScheduledCommandByBatchList } from '../../../store/Commands/CommandsAction';
import ScheduledCommandHistory from '../../CommandsHistory/ScheduledCommandHistory';
import { COMMAND_NAME } from '../../../utils/constants';
import {
  fetchAccessControlFilePath,
  fetchLatestUploadAccessControlCSV,
} from '../../../store/AccessControl/AccessControlActions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    logsContainer: {
      display: 'flex',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
    span: {
      fontWeight: 'bold',
      fontSize: '1rem',
      margin: '10px',
    },
    margin: {
      margin: theme.spacing(1),
    },
    copyToClipBoard: {
      fontSize: '1.5rem',
    },
    copyToClipBoardGrid: { marginTop: 22, marginLeft: 20 },
    buttonStyle: { marginLeft: '10px' },
    dateRangePickerGrid: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '10px',
    },
    leftMargin: {
      marginLeft: '12px',
    },
    datePicker: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    startDateMargin: {
      marginTop: '2px',
    },
    gridItemMargin: {
      marginRight: '144px',
    },
    gridStyle: {
      maxHeight: 400,
      marginLeft: '10px',
      marginBottom: '10px',
    },
    actionButton: {
      display: 'flex',
      justifyContent: 'center',
      padding: '12px 16px',
      borderTop: '1px solid #e5e5e5',
      backgroundColor: '#fafafa',
      width: '800px',
      marginLeft: '10px',
      marginBottom: '10px',
    },
  }),
);

export default function AccessControlScheduledContainer() {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const uploadAccessControlFilePath: string = useSelector(
    (state: ApplicationState) => state.accessControlReducer.filePath,
  );

  const commandSuccessMessage: string = useSelector(
    (state: ApplicationState) => state.assetsReducer.commandSuccessMessage,
  );

  const latestAccessControlCSVFileName: string | undefined = useSelector(
    (state: ApplicationState) => state.accessControlReducer.fileName,
  );

  React.useEffect(() => {
    fetchLatestUploadAccessControlCSV(dispatch);
  }, []);

  React.useEffect(() => {
    if (latestAccessControlCSVFileName) {
      fetchAccessControlFilePath(dispatch, latestAccessControlCSVFileName);
    }
  }, [latestAccessControlCSVFileName]);

  React.useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const [startDate, setStartDate] = React.useState<Date | null>(new Date());
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [showEndDate, setShowEndDate] = React.useState(false);
  React.useState(false);
  const [cronFrequency, setCronFrequency] = React.useState<string>('0 0 * * *');
  const [showCopyToClipBoard, setShowCopyToClipBoard] =
    React.useState<boolean>(false);
  const [commandSend, displayCommandSend] = React.useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowEndDate(e.target.checked);
    if (!e.target.checked) setEndDate(null);
    else setEndDate(new Date());
  };
  const handleCronChange = (newCronValue: string) => {
    setCronFrequency(newCronValue);
  };

  const getParameters = () => {
    return `<${COMMAND_NAME.SCHEDULE_CONFIG_ACCESS_CONTROL}><Path>${uploadAccessControlFilePath}</Path></${COMMAND_NAME.SCHEDULE_CONFIG_ACCESS_CONTROL}>`;
  };

  const setSendToSiteNumberCommand = (
    command: SendToSiteNumbersCommandInfo,
  ) => {
    let scheduledCommandInfo: SendToSiteNumbersScheduledCommandInfo = {
      Division: command.Division,
      SiteNumbers: command.SiteNumbers,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        StartDate: convertDateYYYYMMDD(startDate),
        EndDate: endDate ? convertDateYYYYMMDD(endDate) : null,
        CronFrequency: cronFrequency,
        Parameters: getParameters(),
      },
    };

    sendScheduledToSiteNumberCommand(dispatch, scheduledCommandInfo);

    displayCommandSend(true);
  };

  const setSendToNodeGroupsCommand = (command: SendToNodeGroupsCommandInfo) => {
    let scheduledCommandInfo: SendToNodeGroupsScheduledCommandInfo = {
      Division: command.Division,
      SiteNumber: command.SiteNumber,
      NodeGroups: command.NodeGroups,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        StartDate: convertDateYYYYMMDD(startDate),
        EndDate: endDate ? convertDateYYYYMMDD(endDate) : null,
        CronFrequency: cronFrequency,
        Parameters: getParameters(),
      },
    };

    sendScheduledToNodeGroupsCommand(dispatch, scheduledCommandInfo);

    displayCommandSend(true);
  };

  const setSendToLanesCommand = (command: SendToLanesCommandInfo) => {
    let scheduledCommandInfo: SendToLanesScheduledCommandInfo = {
      Division: command.Division,
      SiteNumber: command.SiteNumber,
      NodeGroup: command.NodeGroup,
      LaneNumbers: command.LaneNumbers,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        StartDate: convertDateYYYYMMDD(startDate),
        EndDate: endDate ? convertDateYYYYMMDD(endDate) : null,
        CronFrequency: cronFrequency,
        Parameters: getParameters(),
      },
    };

    sendScheduledToLanesCommand(dispatch, scheduledCommandInfo);

    displayCommandSend(true);
  };

  const setSendToDivisionCommand = (command: SendToDivisionCommandInfo) => {
    let scheduledCommandInfo: SendToDivisionsScheduledCommandInfo = {
      Divisions: command.Divisions,
      CommandInfo: {
        Command: command.CommandInfo.Command,
        StartDate: convertDateYYYYMMDD(startDate),
        EndDate: endDate ? convertDateYYYYMMDD(endDate) : null,
        CronFrequency: cronFrequency,
        Parameters: getParameters(),
      },
    };
    sendScheduledToDivisionCommand(dispatch, scheduledCommandInfo);

    displayCommandSend(true);
  };

  const resetSuccessMessage = () => {
    clearSuccessMessage(dispatch);
  };

  useEffect(() => {
    let timeoutId: any;

    timeoutId = setTimeout(() => {
      getDateCreatedScheduledCommandByBatchList(
        dispatch,
        COMMAND_NAME.SCHEDULE_CONFIG_ACCESS_CONTROL,
        historyStartDate.toUTCString(),
        useCurrentDate
          ? new Date().toUTCString()
          : historyEndDate.toUTCString(),
      );
      displayCommandSend(false);
      resetSuccessMessage();
    }, 3000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandSuccessMessage]);

  const [historyStartDate, setHistoryStartDate] = React.useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });

  const [historyEndDate, setHistoryEndDate] = React.useState<Date>(new Date());
  const [useCurrentDate, setUseCurrentDate] = React.useState<boolean>(true);

  const onHistoryEndDateChange = (date: Date) => {
    setHistoryEndDate(date);
  };

  const onHistoryStartDateChange = (date: Date) => {
    setHistoryStartDate(date);
  };

  const onUseCurrentDateTimeFilterChange = (useCurrentDate: boolean) => {
    setUseCurrentDate(useCurrentDate);
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <Grid className={classes.margin}>
            <StringResourceManager
              variant="body1"
              style="commandLabel"
              stringKey="SCHEDULED_ACCESS_CONTROL_HEADER"
              gutterBottom={true}
            />
          </Grid>

          <Grid className={classes.margin}>
            <TextBox
              value={maskString(uploadAccessControlFilePath)}
              readOnly={true}
              prefix={() => (
                <>
                  <InputPrefix style={{ backgroundColor: '#EBEBE4' }}>
                    <span className={classes.span}>
                      {GetString('ACCESS_CONTROL_PATH')}
                    </span>
                  </InputPrefix>
                </>
              )}
              suffix={() => (
                <>
                  <InputSuffix>
                    <Button>
                      <CopyToClipboard
                        text={uploadAccessControlFilePath}
                        onCopy={() => setShowCopyToClipBoard(true)}
                      >
                        <ContentCopyIcon />
                      </CopyToClipboard>
                    </Button>
                  </InputSuffix>
                </>
              )}
              style={{ width: 500, height: 38 }}
            />
          </Grid>

          <Grid className={clsx(classes.logsContainer, classes.margin)}>
            <Grid item className={classes.gridItemMargin}>
              <Grid item xs={12}>
                <StringResourceManager
                  variant="body1"
                  style="dialogLabel"
                  stringKey={'DATE_RANGE'}
                  gutterBottom
                />
              </Grid>
              <Grid className={classes.dateRangePickerGrid}>
                <Grid item className={classes.datePicker}>
                  <div className={classes.startDateMargin}>
                    <StringResourceManager
                      variant="body1"
                      style="dialogLabel"
                      stringKey={'START_DATE_RANGE_LABEL'}
                      gutterBottom
                    />
                  </div>
                  <DatePicker
                    className={classes.leftMargin}
                    id="start-date"
                    value={startDate}
                    onChange={e => setStartDate(e.value)}
                    width={210}
                    valid={startDate !== null}
                  ></DatePicker>
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={handleCheckboxChange}
                        name="checkedB"
                        color="primary"
                        checked={showEndDate}
                      />
                    }
                    label={
                      <StringResourceManager
                        variant="body1"
                        style="dialogLabel"
                        stringKey="END_DATE_RANGE_LABEL"
                        gutterBottom
                      />
                    }
                  />
                  {showEndDate && (
                    <DatePicker
                      id="end-date"
                      value={endDate}
                      min={startDate || undefined}
                      onChange={e => setEndDate(e.value)}
                      width={210}
                    ></DatePicker>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid className={clsx(classes.logsContainer, classes.margin)}>
            <Grid item className={classes.gridItemMargin}>
              <Grid item xs={12}>
                <StringResourceManager
                  variant="body1"
                  style="dialogLabel"
                  stringKey={'SELECT_FREQUENCY'}
                  gutterBottom
                />
              </Grid>
              <Grid item xs={12}>
                <Cron value={cronFrequency} setValue={handleCronChange}></Cron>
              </Grid>
            </Grid>
          </Grid>
          <NodeSelection
            title="SELECT_COMMAND_DESTINATION"
            command={'SCHEDULE_CONFIG_ACCESS_CONTROL'}
            commandDropdownlistEnable={false}
            setSendToSiteNumberCommand={setSendToSiteNumberCommand}
            setSendToNodeGroupsCommand={setSendToNodeGroupsCommand}
            setSendToLanesCommand={setSendToLanesCommand}
            buttonTitleKey="SEND_BUTTON"
            setSendToDivisionCommand={setSendToDivisionCommand}
          />
          <ScheduledCommandHistory
            title="SCHEDULED_ACCESS_CONTROL_HISTORY"
            commandName={COMMAND_NAME.SCHEDULE_CONFIG_ACCESS_CONTROL}
            onStartDateChange={onHistoryStartDateChange}
            onEndDateChange={onHistoryEndDateChange}
            onUseCurrentDateTimeFilterChange={onUseCurrentDateTimeFilterChange}
          />
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {commandSend && commandSuccessMessage !== '' && (
                <Notification
                  className={classes.send}
                  type={{
                    style:
                      commandSuccessMessage === 'success' ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        commandSuccessMessage === 'success'
                          ? 'COMMAND_SENT_SUCCESSFULLY'
                          : commandSuccessMessage === 'failed'
                            ? 'COMMAND_SEND_FAILED'
                            : ''
                      }
                    />
                  }
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {showCopyToClipBoard && (
                <Notification
                  className={classes.copyToClipBoard}
                  type={{
                    style: 'success',
                    icon: true,
                  }}
                  closable={true}
                >
                  {<StringResourceManager stringKey={'COPY_TO_CLIPBOARD'} />}
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
}
